import { useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import { inputBaseStyle } from '../../../../_components/TextField.js';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface PhoneFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: { code: string; number: string } | string) => void;
}

// Phone field with country dial code + number. `phoneMode === 'fixed'`
// renders a locked badge for the code (set by the survey author);
// `phoneMode === 'input'` (default) lets the respondent edit it.
// Mobile: type="tel" triggers the numeric phone keypad on iOS / Android,
// inputMode="numeric" for the number portion improves Android coverage.
export function PhoneField({ palette, question, value, onChange }: PhoneFieldProps): JSX.Element {
  const settings = question.settings ?? {};
  const defaultCode = settings.countryCode ?? '+1';
  const phoneMode = settings.phoneMode ?? 'input';

  const objectValue = typeof value === 'object' && value !== null
    ? (value as { code?: string; number?: string })
    : null;
  const initialCode = objectValue?.code ?? defaultCode;
  const initialNumber = objectValue?.number ?? (typeof value === 'string' ? value : '');

  const [code, setCode] = useState(initialCode);
  const [number, setNumber] = useState(initialNumber);
  const [codeFocused, setCodeFocused] = useState(false);
  const [numberFocused, setNumberFocused] = useState(false);

  const update = (nextCode: string, nextNumber: string): void => {
    onChange({ code: nextCode, number: nextNumber });
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {phoneMode === 'fixed' ? (
        <div
          aria-hidden="true"
          style={fixedCodeStyle(palette)}
        >
          {code}
          <LockIcon color={palette.textMuted} />
        </div>
      ) : (
        <input
          type="text"
          inputMode="tel"
          value={code}
          onFocus={() => setCodeFocused(true)}
          onBlur={() => setCodeFocused(false)}
          onChange={(event) => {
            let next = event.currentTarget.value.trim();
            if (next && !next.startsWith('+')) next = `+${next}`;
            setCode(next);
            update(next, number);
          }}
          placeholder="+1"
          aria-label="Country code"
          style={{ ...inputBaseStyle(palette, codeFocused), width: 86, textAlign: 'center' }}
        />
      )}
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={number}
        onFocus={() => setNumberFocused(true)}
        onBlur={() => setNumberFocused(false)}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setNumber(next);
          update(code, next);
        }}
        placeholder={settings.placeholder ?? '(555) 123-4567'}
        aria-label={question.label}
        style={{ ...inputBaseStyle(palette, numberFocused), flex: 1, minWidth: 0 }}
      />
    </div>
  );
}

function fixedCodeStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: 86,
    height: 46,
    padding: '0 10px',
    borderRadius: palette.radius,
    border: `1px solid ${palette.border}`,
    background: palette.card,
    color: palette.text,
    fontSize: 14,
    opacity: 0.85,
    flexShrink: 0,
  };
}

function LockIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
